/**
 * Configurações › Sistema › Segurança do login (I-2).
 *
 * Dois trabalhos numa tela só, porque é assim que o admin usa:
 *   1. AJUSTAR os limites (nada de número hardcoded — endurecer a política
 *      depois de um susto não pode exigir rodada de desenvolvimento);
 *   2. DESTRAVAR alguém agora (o colega ligou dizendo que não entra).
 *
 * O destrave fica ao lado dos limites de propósito: quem aperta o parafuso
 * precisa ver, na mesma tela, quem ficou preso por causa dele.
 */
import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  HelpTip,
  Input,
  Label,
} from '@/components/ui';
import {
  useSegurancaLogin,
  useSegurancaLoginActions,
  rotuloEscopo,
  type LoginRateConfig,
} from '@/lib/seguranca-login-store';

/** Campos editáveis, agrupados como o admin pensa (conta / rede / atraso). */
const GRUPOS: Array<{
  titulo: string;
  descricao: string;
  campos: Array<{ campo: keyof LoginRateConfig; rotulo: string; sufixo?: string }>;
}> = [
  {
    titulo: 'Por conta',
    descricao:
      'Quantos erros de senha uma mesma conta pode cometer antes de ficar travada, e por quanto tempo.',
    campos: [
      { campo: 'contaLimite', rotulo: 'Erros até travar' },
      { campo: 'contaJanelaMin', rotulo: 'Contados dentro de', sufixo: 'min' },
      { campo: 'contaBloqueioMin', rotulo: 'Tempo travado', sufixo: 'min' },
    ],
  },
  {
    titulo: 'Por endereço de rede (IP)',
    descricao:
      'Vale mais largo de propósito: um prédio, um escritório ou uma operadora de celular colocam dezenas de pessoas na mesma saída de internet. Apertar demais aqui derruba gente inocente junto.',
    campos: [
      { campo: 'ipLimite', rotulo: 'Erros até travar' },
      { campo: 'ipJanelaMin', rotulo: 'Contados dentro de', sufixo: 'min' },
      { campo: 'ipBloqueioMin', rotulo: 'Tempo travado', sufixo: 'min' },
    ],
  },
  {
    titulo: 'Atraso e resposta',
    descricao:
      'A partir do erro indicado, a resposta passa a demorar de propósito. O tempo mínimo de resposta vale pra todas as tentativas — é o que impede alguém de descobrir quais e-mails existem cronometrando o sistema.',
    campos: [
      { campo: 'atrasoAPartirDe', rotulo: 'Começa a atrasar no erro nº' },
      { campo: 'atrasoPassoMs', rotulo: 'Atraso por erro', sufixo: 'ms' },
      { campo: 'atrasoMaxMs', rotulo: 'Atraso máximo', sufixo: 'ms' },
      { campo: 'respostaMinMs', rotulo: 'Tempo mínimo de resposta', sufixo: 'ms' },
    ],
  },
  {
    titulo: 'Cofre de senhas',
    descricao:
      'Limite próprio pra revelação de senhas guardadas. Aqui quem age já está logado — o teto existe pra que ninguém consiga copiar o cofre inteiro de uma vez.',
    campos: [
      { campo: 'cofreContaLimite', rotulo: 'Revelações por pessoa' },
      { campo: 'cofreContaJanelaMin', rotulo: 'Contadas dentro de', sufixo: 'min' },
      { campo: 'cofreContaBloqueioMin', rotulo: 'Tempo travado', sufixo: 'min' },
    ],
  },
  {
    titulo: 'Aviso e histórico',
    descricao:
      'Quantos bloqueios seguidos na mesma conta disparam o aviso no sininho, e por quanto tempo o histórico de tentativas fica guardado.',
    campos: [
      { campo: 'notificarAdminApos', rotulo: 'Bloqueios seguidos até avisar' },
      { campo: 'retencaoHoras', rotulo: 'Guardar histórico por', sufixo: 'h' },
    ],
  },
];

function formatarRestante(segundos: number): string {
  if (segundos <= 60) return 'menos de 1 minuto';
  const min = Math.ceil(segundos / 60);
  return `${min} minuto${min > 1 ? 's' : ''}`;
}

export function SegurancaLoginConfigCard() {
  const { data, isLoading, error } = useSegurancaLogin();
  const { salvarConfig, destravar } = useSegurancaLoginActions();
  const [rascunho, setRascunho] = useState<Partial<Record<keyof LoginRateConfig, string>>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  // Recarrega o rascunho quando os valores do servidor chegam/mudam. O servidor
  // é a fonte da verdade: se ele recusar um valor (piso/teto), a tela precisa
  // voltar a mostrar o que ficou valendo, não o que foi digitado.
  useEffect(() => {
    if (!data?.config) return;
    const inicial: Partial<Record<keyof LoginRateConfig, string>> = {};
    for (const g of GRUPOS) {
      for (const c of g.campos) inicial[c.campo] = String(data.config[c.campo]);
    }
    setRascunho(inicial);
  }, [data?.config]);

  const sujo =
    !!data?.config &&
    GRUPOS.some((g) =>
      g.campos.some((c) => (rascunho[c.campo] ?? '') !== String(data.config[c.campo])),
    );

  async function salvar() {
    if (!data?.config) return;
    setErro(null);
    setSalvo(false);
    const parcial: Partial<LoginRateConfig> = {};
    for (const g of GRUPOS) {
      for (const c of g.campos) {
        const bruto = (rascunho[c.campo] ?? '').trim();
        const n = Number(bruto);
        if (bruto === '' || !Number.isFinite(n)) {
          setErro(`"${c.rotulo}" precisa de um número.`);
          return;
        }
        parcial[c.campo] = Math.round(n);
      }
    }
    try {
      await salvarConfig.mutateAsync(parcial);
      setSalvo(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
    }
  }

  const bloqueios = data?.bloqueios ?? [];

  return (
    <Card id="seguranca-login" className="mb-6 min-w-0 p-5" data-testid="seguranca-login-config">
      <div className="mb-1 flex flex-wrap items-center gap-1">
        <h3 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Segurança do login</h3>
        <HelpTip texto="Quantos erros de senha o sistema aceita antes de travar o acesso, por quanto tempo, e quem está travado neste momento. Vale pro login da equipe, pro portal do cliente, pra portaria e pra recuperação de senha." />
      </div>
      <p className="mb-4 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">
        O bloqueio é sempre temporário — não existe conta bloqueada pra sempre. Se alguém ficou
        preso por engano, destrave aqui embaixo e o acesso volta na hora.
      </p>

      {error && (
        <Alert variant="danger" rail className="mb-4">
          Não foi possível carregar as configurações de segurança.
        </Alert>
      )}
      {erro && (
        <Alert variant="danger" rail className="mb-4">
          {erro}
        </Alert>
      )}
      {salvo && !sujo && (
        <Alert variant="success" rail className="mb-4">
          Limites salvos. Valem a partir da próxima tentativa de login.
        </Alert>
      )}

      {isLoading && <p className="text-sm text-ink-2 dark:text-ink-dark-2">Carregando…</p>}

      {data?.config && (
        <>
          <div className="mb-6 space-y-6">
            {GRUPOS.map((g) => (
              <div key={g.titulo} className="min-w-0">
                <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">{g.titulo}</h4>
                <p className="mb-3 max-w-[68ch] text-xs text-ink-2 dark:text-ink-dark-2">
                  {g.descricao}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {g.campos.map((c) => (
                    <div key={c.campo} className="min-w-0">
                      <Label htmlFor={`seg-${c.campo}`} className="text-xs">
                        {c.rotulo}
                        {c.sufixo ? ` (${c.sufixo})` : ''}
                      </Label>
                      <Input
                        id={`seg-${c.campo}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={rascunho[c.campo] ?? ''}
                        onChange={(e) =>
                          setRascunho((r) => ({ ...r, [c.campo]: e.target.value }))
                        }
                        data-testid={`seg-input-${c.campo}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Button
            onClick={() => void salvar()}
            disabled={!sujo || salvarConfig.isPending}
            loading={salvarConfig.isPending}
            data-testid="seg-salvar"
          >
            Salvar limites
          </Button>
        </>
      )}

      {/* ── Quem está travado agora ────────────────────────────────────── */}
      <div className="mt-8 min-w-0 border-t border-line pt-6 dark:border-line-dark">
        <div className="mb-1 flex flex-wrap items-center gap-1">
          <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Acessos travados agora
          </h4>
          <HelpTip texto="Contas e endereços de rede que atingiram o limite. Destravar devolve o acesso na hora, sem esperar o prazo." />
        </div>

        {bloqueios.length === 0 ? (
          <EmptyState
            title="Ninguém travado"
            description="Nenhuma conta ou endereço atingiu o limite de tentativas."
          />
        ) : (
          <ul className="space-y-2">
            {bloqueios.map((b) => (
              <li
                key={`${b.escopo}:${b.chaveTipo}:${b.chave}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2 dark:border-line-dark"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm text-ink-1 dark:text-ink-dark-1">
                      {b.chave}
                    </span>
                    <Badge variant={b.chaveTipo === 'conta' ? 'neutral' : 'warning'}>
                      {b.chaveTipo === 'conta' ? 'conta' : 'rede'}
                    </Badge>
                  </div>
                  <p className="text-xs text-ink-2 dark:text-ink-dark-2">
                    {rotuloEscopo(b.escopo)} · libera sozinho em{' '}
                    {formatarRestante(b.segundosRestantes)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() =>
                    void destravar.mutateAsync({
                      escopo: b.escopo,
                      chaveTipo: b.chaveTipo,
                      chave: b.chave,
                    })
                  }
                  disabled={destravar.isPending}
                  data-testid="seg-destravar"
                >
                  Destravar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
